/**
 * Utility functions for Solution Import operations
 * 
 * This file contains pure helper functions extracted from SolutionImportModal
 * to improve code organization and testability.
 */

/**
 * Check if a field needs to be created
 * Returns true if the value is empty or starts with "NEW:"
 */
export const needsCreation = (value: any): boolean => {
  if (!value) return true;
  if (typeof value === "string") {
    return value.trim() === "" || value.startsWith("NEW:");
  }
  return false;
};

/**
 * Helper function to check if a field is empty/unmapped (for UI validation)
 * Returns true if the value is empty but doesn't have a "NEW:" prefix
 */
export const isEmptyOrUnmapped = (value: any): boolean => {
  if (!value) return true;
  if (typeof value === "string") {
    return value.trim() === "" && !value.startsWith("NEW:");
  }
  return false;
};

/**
 * Helper function to check if manually managed field has "NEW:" but no name
 */
export const hasEmptyNewFieldName = (value: any): boolean => {
  if (!value) return false;
  if (typeof value === "string") {
    return value === "NEW:" || value.trim() === "NEW:";
  }
  return false;
};

/**
 * Field type mappings used in solution imports
 */
export const FIELD_TYPE_MAPPINGS = {
  ApiConnection: "",
  ContactField: "ContactFields",
  ContactFieldOption: "",
  ContactSet: "ContactGroups",
  ReportingTemplate: "ReportingTemplates",
  ReportingTemplateField: "",
  Topic: "ReportingTopics",
} as const;

/**
 * Generate a label for imported items
 * If customLabel starts with "NEW:", use the custom name
 * Otherwise, append a timestamp to the original label
 */
export const generateImportedLabel = (
  originalLabel: string,
  customLabel?: string
): string => {
  if (customLabel && customLabel.startsWith("NEW:")) {
    return customLabel.substring(4); // Remove "NEW:" prefix
  }
  return `${originalLabel} (Imported - ${new Date().toISOString()})`;
};

/**
 * Initialize empty external field updates structure
 */
export const createEmptyFieldUpdates = () => ({
  ContactField: {},
  ContactFieldOption: {},
  ContactSet: {},
  ReportingTemplate: {},
  ReportingTemplateField: {},
  Topic: {},
  ApiConnection: {},
});
